import { useCurrentUser } from '@/hooks/useCurrentUser';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { Button, Container, Paper, Stack, Textarea, Title, Text, Alert } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconAlertCircle } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';

export function ImportHealthiePatientsPage(): ReactElement {
  const medplum = useMedplum();
  const {
    currentUserPermissions: { isAdmin, hasAdminMembership },
  } = useCurrentUser();
  const [patientIds, setPatientIds] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmedIds = patientIds.trim();

    if (!trimmedIds) {
      alert.error('Enter at least one Healthie patient ID.');
      return;
    }

    // Parse the IDs - support comma-separated or newline-separated
    const idsArray = trimmedIds
      .split(/[,\n]/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (idsArray.length === 0) {
      alert.error('No valid patient IDs found.');
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await fetchBot(medplum, 'healthie-import-by-ids', {
        healthiePatientIds: idsArray,
      });

      const { synced, failed, message } = result as {
        synced: number;
        failed: number;
        errors: string[];
        message: string;
      };

      if (failed === 0) {
        alert.success(message || `Successfully imported ${synced} patient(s).`);
        // Clear the form on success
        setPatientIds('');
      } else {
        alert.warning(
          message || `Import completed with issues: ${synced} succeeded, ${failed} failed. Check console for details.`
        );
      }
    } catch (error) {
      alert.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="lg" shadow="sm">
        <Title order={2} mb="lg">
          Import Healthie Patients
        </Title>

        {!isAdmin && !hasAdminMembership && (
          <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red" mb="md">
            This page is only accessible to administrators.
          </Alert>
        )}

        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Enter Healthie patient IDs to import. You can enter multiple IDs separated by commas or newlines.
          </Text>

          <Textarea
            label="Healthie Patient IDs"
            placeholder="123, 456, 789&#10;or&#10;123&#10;456&#10;789"
            value={patientIds}
            onChange={(event) => setPatientIds(event.currentTarget.value)}
            minRows={6}
            required
            description="Enter Healthie patient IDs separated by commas or newlines"
          />

          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={(!isAdmin && !hasAdminMembership) || !patientIds.trim()}
            fullWidth
          >
            Import Patients
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
